import { SearchOutlined } from '@ant-design/icons';
import { Modal } from 'antd';

import styled from 'styled-components';

import { Flex, Input, Text } from 'src/client/components';

export const StyledModal = styled(Modal)`
  .ant-modal-body {
    padding: 0;
    height: 90vh !important;
    display: flex;
    flex-direction: column;
  }

  .ant-modal-content {
    border-radius: 8px;
    height: 90vh !important;
  }

  top: 40px !important;
  width: 90vw !important;
  max-width: 1440px !important;
`;

export const ModalHeaderContainer = styled.div`
  background: ${(props) => props.theme.colors.randomizeYellow};
  overflow: hidden;
  position: relative;
  flex-shrink: 0;

  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
`;

export const ModalHeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;

  padding: 24px;

  @media ${(props) => props.theme.screen.md} {
    flex-direction: row;
    align-items: center;
    gap: 24px;
  }
`;

export const ModalHeaderTextSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  max-width: 720px;
  width: 100%;

  @media ${(props) => props.theme.screen.md} {
    width: 50%;
    flex-shrink: 0;
    padding: 14px 0px 14px 0;
  }

  @media ${(props) => props.theme.screen.lg} {
    width: 60%;
  }

  @media ${(props) => props.theme.screen.xl} {
    width: 70%;
  }

  & p:first-child {
    @media ${(props) => props.theme.screen.md} {
      font-size: 30px;
    }
  }

  & p:nth-child(2) {
    font-weight: 510;
    margin-bottom: 8px;

    @media ${(props) => props.theme.screen.sm} {
      font-size: inherit;
      line-height: inherit;
    }
  }
`;

export const ModalHeaderIllustration = styled.div`
  display: none;

  @media ${(props) => props.theme.screen.md} {
    display: flex;
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    align-items: center;
    justify-content: center;
    padding-right: 24px;
  }
`;

export const IllustrationImage = styled.img`
  width: auto;
  height: auto;
  object-fit: contain;

  @media ${(props) => props.theme.screen.md} {
    max-width: 280px;
    max-height: 140px;
  }

  @media ${(props) => props.theme.size.xl} {
    max-width: 320px;
    max-height: 160px;
  }
`;

export const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;

  padding: 24px 24px 0;

  flex: 1;
  min-height: 0;
  overflow: hidden;

  @media (min-width: 640px) {
    gap: 28px;
  }

  @media (min-width: 768px) {
    gap: 32px;
  }
`;

export const InfoText = styled(Text)`
  font-weight: ${(props) => props.theme.fontWeights.regular};
  flex-shrink: 0;
`;

export const SelectedRecipientsContainer = styled.div`
  flex-shrink: 0;
  overflow-y: auto;
  max-height: 60px;

  @media ${(props) => props.theme.screen.md} {
    max-height: 120px;
  }
`;

export const RecipientSelectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

export const SearchRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media ${(props) => props.theme.screen.md} {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

export const SearchContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
  width: 60%;
`;

export const SearchInput = styled(Input)`
  .ant-input {
    border-radius: 8px;
    padding: 12px 16px;
    font-size: 14px;

    &::placeholder {
      color: ${(props) => props.theme.colors.darkEmphasis2};
    }
  }

  .ant-input-suffix {
    color: ${(props) => props.theme.colors.darkEmphasis2};
  }
`;

export const SearchIcon = styled(SearchOutlined)`
  color: ${(props) => props.theme.colors.darkEmphasis2};
`;

export const RecipientSelectionHeader = styled(Text)`
  flex-shrink: 0;
`;

export const ScrollableGridContainer = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: scroll;
  overflow-x: hidden;

  padding: 8px 4px 8px;

  &::-webkit-scrollbar {
    width: 16px;
    background: transparent;
  }

  &::-webkit-scrollbar-track {
    background: ${(props) => props.theme.colors.gray4};
    border-radius: 8px;
    margin: 6px 0;
  }

  &::-webkit-scrollbar-thumb {
    background: ${(props) => props.theme.colors.gray5};
    border-radius: 8px;
    border: 2px solid ${(props) => props.theme.colors.gray4};

    &:hover {
      background: ${(props) => props.theme.colors.gray2};
      border-color: ${(props) => props.theme.colors.gray3};
    }

    &:active {
      background: ${(props) => props.theme.colors.darkEmphasis2};
    }
  }

  /* scrollbar-width: thin; */
  scrollbar-track-color: ${(props) => props.theme.colors.gray4};
`;

export const RecipientsGrid = styled.div`
  display: grid;
  gap: 16px;
  width: 100%;

  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (min-width: 480px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 6px;
  }

  @media ${(props) => props.theme.screen.sm} {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  @media ${(props) => props.theme.screen.md} {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }

  @media ${(props) => props.theme.device.laptop} {
    grid-template-columns: repeat(7, minmax(0, 1fr));
  }

  @media (min-width: 1124px) {
    grid-template-columns: repeat(8, minmax(0, 1fr));
  }

  @media ${(props) => props.theme.screen.xl} {
    grid-template-columns: repeat(9, minmax(0, 1fr));
  }

  @media ${(props) => props.theme.screen.xxl} {
    max-width: 1400px;
    margin: 0 auto;
  }
`;

export const FooterButtonGroup = styled(Flex)`
  gap: 8px;
  padding: 16px 24px;
  justify-content: flex-end;
`;

export const ItalicText = styled.span`
  font-family: inherit;
  font-style: italic;
`;
